const GA_ID = 'G-KKHZWCHSSN';

// ─── Anonymous User-ID ────────────────────────────────────────────────────────
// Gera um ID anônimo persistente para conectar sessões do mesmo visitante.
// Não é PII — é um UUID aleatório gerado localmente.
// Segue as regras do GA4: < 256 chars, não é email/nome/CPF.
const ANON_ID_KEY = 'md_ga_uid';

function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      // Gera UUID v4 simples sem dependência externa
      id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return null; // localStorage bloqueado (modo privado restrito)
  }
}

/**
 * Seta o user_id no GA4.
 * Deve ser chamado ANTES de qualquer evento.
 * Regras GA4: não pode ser PII, < 256 chars, null para deslogar.
 */
export const setGaUserId = (userId) => {
  if (!window.gtag) return;
  // null limpa o user_id (logout) — não usar "" nem "null" string
  window.gtag('set', { user_id: userId ?? null });
};

/**
 * Inicializa o User-ID anônimo assim que o GA4 estiver disponível.
 * Chamado uma vez no App.js após o gtag carregar.
 */
export const initGaUserId = () => {
  const id = getOrCreateAnonId();
  if (id) setGaUserId(id);
};

/**
 * Atualiza o user_id quando o lead é capturado (popup de cupom).
 * Usa o couponCode como identificador — não é PII.
 * Conecta a sessão anônima ao lead no GA4.
 */
export const setGaLeadId = (couponCode) => {
  if (!couponCode) return;
  // Prefixo "lead_" + código do cupom — não é PII, < 256 chars
  setGaUserId(`lead_${couponCode}`);
  try {
    localStorage.setItem(ANON_ID_KEY, `lead_${couponCode}`);
  } catch {}
};

// ─── UTM helpers ─────────────────────────────────────────────────────────────

/**
 * Extrai parâmetros UTM da URL atual.
 * Retorna objeto com os valores encontrados (omite os ausentes).
 */
function getUtmParams(search) {
  const params = new URLSearchParams(search || window.location.search);
  const utm = {};
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  keys.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return utm;
}

/**
 * Envia page_view ao GA4 com path + UTM completos.
 * Chamado pelo PageTracker a cada mudança de rota.
 */
export const trackPageView = (path) => {
  if (!window.gtag) return;

  const utm = getUtmParams(window.location.search);

  window.gtag('config', GA_ID, {
    page_path: path,
    // Passa a URL completa para o GA4 processar UTM automaticamente
    page_location: window.location.href,
    // Repassa UTM explicitamente como campaign_* para garantir atribuição
    ...(utm.utm_source   && { campaign_source:  utm.utm_source }),
    ...(utm.utm_medium   && { campaign_medium:  utm.utm_medium }),
    ...(utm.utm_campaign && { campaign_name:    utm.utm_campaign }),
    ...(utm.utm_term     && { campaign_term:    utm.utm_term }),
    ...(utm.utm_content  && { campaign_content: utm.utm_content }),
  });
};

/**
 * Envia evento customizado ao GA4.
 * Inclui UTM automaticamente se presentes na URL.
 */
export const trackEvent = (eventName, params = {}) => {
  if (!window.gtag) return;

  const utm = getUtmParams(window.location.search);

  window.gtag('event', eventName, {
    ...params,
    // Anexa UTM ao evento para segmentação por campanha nos relatórios
    ...(utm.utm_source   && { campaign_source:  utm.utm_source }),
    ...(utm.utm_medium   && { campaign_medium:  utm.utm_medium }),
    ...(utm.utm_campaign && { campaign_name:    utm.utm_campaign }),
    ...(utm.utm_term     && { campaign_term:    utm.utm_term }),
    ...(utm.utm_content  && { campaign_content: utm.utm_content }),
  });
};
